import { useState, useRef, useEffect } from 'react';
import { X, Send, MessageSquare, Mic, MicOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/use-toast';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface SpeechRecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}

interface SpeechRecognitionEvent {
  results: ArrayLike<ArrayLike<{ transcript: string }>>;
}

interface CustomWindow {
  SpeechRecognition?: new () => SpeechRecognitionInstance;
  webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
}

export const AIAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: "Hey! I'm Harshit's AI assistant. Ask me anything about his projects, tech stack, or how to work together."
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const { toast } = useToast();

  const sendMessageRef = useRef<(text?: string) => Promise<void>>(() => Promise.resolve());

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    const win = window as unknown as CustomWindow;
    if (win.SpeechRecognition || win.webkitSpeechRecognition) {
      const SpeechRecognition = win.SpeechRecognition || win.webkitSpeechRecognition;
      if (SpeechRecognition) {
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = false;
        recognitionRef.current.interimResults = false;

        recognitionRef.current.onresult = (event: SpeechRecognitionEvent) => {
          console.log('Voice recognized:', event.results[0][0].transcript);
          const transcript = event.results[0][0].transcript;
          setInput(transcript);
          sendMessageRef.current(transcript);
        };

        recognitionRef.current.onerror = (event: { error: string }) => {
          console.error('Speech recognition error:', event.error);
          setIsRecording(false);
          if (event.error !== 'no-speech' && event.error !== 'aborted') {
            toast({
              title: "Error",
              description: `Could not access microphone: ${event.error}`,
              variant: "destructive",
            });
          }
        };

        recognitionRef.current.onend = () => {
          console.log('Speech recognition ended');
          setIsRecording(false);
        };

        console.log('Speech recognition initialized');
      }
    } else {
      console.error('Speech recognition not supported');
    }
  }, [toast]);

  const sendMessage = async (messageText?: string) => {
    const textToSend = messageText || input;
    if (!textToSend.trim() || isLoading) return;

    const userMessage: Message = { role: 'user', content: textToSend };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const apiKey = import.meta.env.VITE_GROQ_API_KEY || "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";
      
      const response = await fetch("https://api.groq.com/openai/v1/chat/completions", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${apiKey}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          model: "llama-3.3-70b-versatile",
          messages: [
            {
              role: "system",
              content: `You are the AI assistant for Harshit's builder portfolio (harshitpraj.online).

About Harshit:
- Role: Full Stack & AI Developer, Solo Product Builder based in India.
- Philosophy: Builds in public, ships fast, crafts clean minimalist applications with zero bloat.
- Primary Tech Stack: React, TypeScript, Node.js, Tailwind CSS, Supabase, Groq API (Llama 3.3 70B), Vite, Cloudflare, Python.
- Featured Projects:
  1. FinishAI — AI-powered project completion and goal execution tool.
  2. RingTrack — Smart ring health metrics tracking dashboard.
  3. IngreCheck — Food & cosmetic ingredient safety checker powered by Groq.
  4. AI Companion Chat — Fast WhatsApp-style AI companion web app.
  5. Personal Portfolio & Admin Showcase — Builder showcase with real-time Supabase management.
- Contact & Links:
  - Email: prajharshit@gmail.com
  - Twitter / X: @harshitpraj (https://x.com/harshitpraj)
  - GitHub: ha1rshit (https://github.com/ha1rshit)
  - LinkedIn: harshitpraj (https://www.linkedin.com/in/harshitpraj)

Tone & Response Guidelines:
- Direct, sharp, friendly, and authentic — sound like a fellow builder talking, not a generic corporate bot.
- Avoid artificial fluff, robotic formal intros, or corporate buzzwords.
- Keep answers clear, concise, and straight to the point (1-3 short paragraphs or bullet points).
- Accurately share Harshit's project details, tech stack, and contact links when asked.`
            },
            ...messages.map(m => ({
              role: m.role,
              content: m.content
            })),
            {
              role: "user",
              content: textToSend
            }
          ]
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error?.message || `HTTP error ${response.status}`);
      }

      const data = await response.json();
      const text = data.choices?.[0]?.message?.content || "I'm sorry, I couldn't generate a response.";
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: text
      }]);
    } catch (error) {
      console.error("Groq API error:", error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error communicating with the AI service. Please try again.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    sendMessageRef.current = sendMessage;
  });

  const toggleRecording = () => {
    console.log('Toggle recording clicked, current state:', isRecording);
    if (!recognitionRef.current) {
      console.error('Recognition not available');
      toast({
        title: "Not Supported",
        description: "Voice recognition is not supported in your browser",
        variant: "destructive",
      });
      return;
    }

    if (isRecording) {
      console.log('Stopping recording');
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      try {
        console.log('Starting recording');
        recognitionRef.current.start();
        setIsRecording(true);
      } catch (error) {
        console.error('Failed to start recording:', error);
        toast({
          title: "Error",
          description: "Failed to start recording",
          variant: "destructive",
        });
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 w-12 h-12 sm:w-14 sm:h-14 bg-black text-white shadow-lg hover-scale flex items-center justify-center z-50 animate-fade-in rounded-none border border-white/20"
          aria-label="Open AI Assistant"
        >
          <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
      )}

      {/* Chat Widget */}
      {isOpen && (
        <div className="fixed inset-x-3 bottom-3 top-auto h-[82vh] max-h-[640px] sm:inset-auto sm:bottom-8 sm:right-8 sm:w-96 sm:h-[600px] bg-white dark:bg-card border-2 border-black dark:border-white shadow-2xl z-50 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-3 sm:p-4 border-b-2 border-black dark:border-white bg-white dark:bg-card">
            <div>
              <h3 className="font-bold text-base sm:text-lg text-black dark:text-white">AI ASSISTANT</h3>
              <p className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-300">ASK ABOUT HARSHIT</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-gray-100 dark:hover:bg-neutral-800 p-2 hover-scale text-black dark:text-white"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-3 sm:p-4" ref={scrollRef}>
            <div className="space-y-3 sm:space-y-4">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  style={{
                    animation: 'fade-in 0.3s ease-out, scale-in 0.2s ease-out',
                    animationDelay: `${index * 0.05}s`
                  }}
                >
                  <div
                    className={`max-w-[85%] sm:max-w-[80%] p-2.5 sm:p-3 text-xs sm:text-sm ${
                      message.role === 'user'
                        ? 'bg-black text-white dark:bg-white dark:text-black font-medium'
                        : 'bg-gray-100 text-black dark:bg-neutral-900 dark:text-white border border-gray-300 dark:border-neutral-800'
                    }`}
                  >
                    <p className="leading-relaxed whitespace-pre-wrap animate-fade-in">
                      {message.content.split('').map((char, i) => (
                        <span
                          key={i}
                          style={{
                            animation: 'fade-in 0.05s ease-out',
                            animationDelay: `${i * 0.01}s`,
                            animationFillMode: 'backwards'
                          }}
                        >
                          {char}
                        </span>
                      ))}
                    </p>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start animate-fade-in">
                  <div className="bg-gray-100 dark:bg-neutral-900 text-black dark:text-white border border-gray-300 dark:border-neutral-800 p-3">
                    <p className="text-xs sm:text-sm animate-pulse font-medium">Thinking...</p>
                  </div>
                </div>
              )}
              {isRecording && (
                <div className="flex justify-center animate-fade-in my-2">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-red-100 dark:bg-red-950/60 border border-red-500">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    <p className="text-xs text-red-700 dark:text-red-300 font-medium">Listening...</p>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="p-3 sm:p-4 border-t-2 border-black dark:border-white bg-white dark:bg-card">
            <div className="flex gap-1.5 sm:gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask a question..."
                className="flex-1 border-2 border-black dark:border-white focus:ring-0 focus:ring-offset-0 text-xs sm:text-sm h-10"
                disabled={isLoading || isRecording}
              />
              <Button
                onClick={toggleRecording}
                disabled={isLoading}
                variant={isRecording ? "destructive" : "outline"}
                className="border-2 border-black dark:border-white px-2.5 sm:px-4 h-10 hover-scale"
                title={isRecording ? "Stop listening" : "Voice input"}
              >
                {isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </Button>
              <Button
                onClick={() => sendMessage()}
                disabled={isLoading || !input.trim() || isRecording}
                className="bg-black text-white dark:bg-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 px-3 sm:px-4 h-10 hover-scale"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
